<template>
  <div class="pa-4">
    <h2 class="ml-4 my-4">{{ $t('message.documents') }}</h2>
    <div v-if="loading" class="text-center">
      <circles-loader></circles-loader>
    </div>
    <div v-else-if="document" class="text-center">
      <v-card-text class="mb-0 pb-0">
        <v-text-field hide-details :label="$t('message.name')" outlined dense v-model="document.name"></v-text-field>
        <v-text-field hide-details counter="50" maxlength="50" v-if="document.public"
          :label="$t('message.short_description')" outlined dense v-model="document.shortDescription"></v-text-field>
      </v-card-text>
      <v-card-actions class="ml-2" v-if="!isSmallerDisplay()">
        <v-switch hide-details class="mr-2" v-model="document.active" :label="$t('message.active')"></v-switch>
        <v-switch hide-details class="mr-2" v-if="!document.public" v-model="document.required"
          :label="$t('message.required')"></v-switch>
        <v-switch hide-details class="mr-2" v-if="!document.required && !document.contract" v-model="document.public"
          :label="$t('message.public')"></v-switch>
        <v-switch hide-details class="mr-2" v-if="documentId == 'new'" :disabled="document.contract"
          v-model="document.survey" :label="$t('message.survey')"></v-switch>
        <v-switch hide-details class="mr-2" :disabled="document.survey" v-model="document.contract"
          :label="$t('message.contract')"></v-switch>
      </v-card-actions>
      <div class="ml-2" v-else>
        <div>
          <v-switch hide-details class="mr-1" v-model="document.active" :label="$t('message.active')"></v-switch>
        </div>
        <div>
          <v-switch hide-details class="mr-1" v-model="document.required" :label="$t('message.required')"></v-switch>
        </div>
        <div>
          <v-switch hide-details class="mr-1" v-if="!document.required && !document.contract" v-model="document.public"
            :label="$t('message.public')"></v-switch>
        </div>
        <div>
          <v-switch hide-details class="mr-1" v-if="documentId == 'new'" :disabled="document.contract"
            v-model="document.survey" :label="$t('message.survey')"></v-switch>
        </div>
        <div>
          <v-switch hide-details class="mr-1" :disabled="document.survey" v-model="document.contract"
            :label="$t('message.contract')"></v-switch>
        </div>
      </div>
      <v-card elevation="0">
        <div class="pl-2 mt-2 text-left text-subtitle-1">{{ $t('message.languages') }}</div>
        <v-card-actions v-if="!document.survey">
          <v-btn elevation="0" outlined small :block="true" @click="displayAddNewLangDialog()">{{
            $t('message.add_new_language') }}</v-btn>
        </v-card-actions>
        <v-card-actions v-if="!document.survey && availableLanguages">
          <v-select @change="orderLanguages" hide-details outlined dense v-model="primaryLanguage"
            :items="availableLanguages" :label="$t('message.primary_language')"></v-select>
        </v-card-actions>
        <v-card-actions v-if="!document.survey">
          <template v-for="l in availableLanguages">
            <v-tooltip v-if="l == selectedLang && availableLanguages.length > 0" :key="l" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn elevation="0" class="ml-2" v-bind="attrs" v-on="on"
                  @click="editLanguageDialog = true; languageField = l" :color="l == selectedLang ? 'accent' : ''" fab
                  small dark :key="l">{{ l }}</v-btn>
              </template>
              <span>{{ $t('message.click_to_remove_language') }}</span>
            </v-tooltip>
            <v-tooltip v-else :key="l" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn elevation="0" class="ml-2" v-bind="attrs" v-on="on" @click="switchLang(l)"
                  :color="l == selectedLang ? 'pink' : ''" fab small dark :key="l">{{ l }}</v-btn>
              </template>
              <span>{{ $t('message.click_to_select_language') }}</span>
            </v-tooltip>
          </template>
        </v-card-actions>
      </v-card>
      <v-card-text>
        <vue-editor v-model="documentContents" :editor-toolbar="editorToolbar" />
      </v-card-text>
      <v-card-text v-if="document && document.lastUpdatedBy" class="text-left mt-0 pt-0">
        <v-avatar size="20">
          <img v-if="document.lastUpdatedBy.userImageSmall" :src="userImg(document.lastUpdatedBy.userImageSmall)" />
          <img v-else :src="assets('default-profile-avatar.png')" />
        </v-avatar>
        {{ $t('message.last_update_by') }}
        {{ document.lastUpdatedBy.displayName }}
        <span style="color:gray">{{ $d(new Date(document.lastUpdatedOn), 'short', $i18n.locale) }}</span>
      </v-card-text>
      <v-card-actions v-if="document.public" class="text-left">
        <v-btn elevation="0" @click="copyPublicUrl()">
          <v-icon class="mr-1" size="20">mdi-content-copy</v-icon>
          {{ $t('message.copy_url') }}
        </v-btn>
      </v-card-actions>
      <v-card v-if="document.survey" class="text-left elevation-0" outlined>
        <v-card-actions style="margin-left:15px">
          <span class="text-subtitle-1">{{ $t('message.questions') }}</span>
          <v-spacer></v-spacer>
          <v-btn elevation="0" v-if="newQuestion == null" @click="initializeNewQuestion()" small>+
            {{ $t('message.add_question') }}</v-btn>
        </v-card-actions>
        <v-divider></v-divider>
        <v-card v-if="newQuestion" class="pa-2" elevation="0">
          <v-card-text>
            <v-select :label="$t('message.answer_type')" :items="answerTypes"
              v-model="newQuestion.answerType"></v-select>
            <v-text-field v-model="newQuestion.question" :label="$t('message.question')"></v-text-field>
            <v-card v-if="newQuestion.answerType == 'multiple_choice'" elevation="0">
              <v-card-text class="supporting-text">
                {{ $t('message.at_least_2_options') }}
              </v-card-text>
              <v-card-text>
                <v-text-field v-bind:key="'new_question_option_' + index"
                  v-for="(i, index) in newQuestion.answerOptions"
                  :label="$t('message.answer_option') + ' ' + (index + 1)" v-model="newQuestion.answerOptions[index]"
                  maxlength="30" counter>
                </v-text-field>
              </v-card-text>
            </v-card>
            <v-checkbox :label="$t('message.required')" v-model="newQuestion.required"></v-checkbox>
          </v-card-text>
          <v-card-actions>
            <v-btn elevation="0" small @click="addNewQuestion()" v-if="newQuestion.question && !newQuestion._id">
              <v-icon>mdi-plus-circle-outline</v-icon>{{ $t('message.add') }}
            </v-btn>
            <v-btn elevation="0" small @click="editQuestion(newQuestion.index)"
              v-if="newQuestion.question && newQuestion._id">
              <v-icon>mdi-pencil-box-outline</v-icon>{{ $t('message.edit') }}
            </v-btn>
            <v-btn elevation="0" small color="secondary" @click="newQuestion = null">
              {{ $t('message.close') }}
            </v-btn>
          </v-card-actions>
        </v-card>
        <div v-if="questions" class="mt-2">
          <template v-for="(i, index) in questions">
            <v-card :key="'question_' + index" class="elevation-0" flat>
              <v-card-title>
                <v-text-field :label="$t('message.question') + ' ' + (index + 1)" v-model="i.question"></v-text-field>
              </v-card-title>
              <v-card-text class="supporting-text" style="margin-top:-15px;">
                <b>{{ $t('message.answer_type') }}:</b>
                <span v-if="i.answerType == 'boolean'">{{ $t('message.true_false') }}</span>
                <span v-else-if="i.answerType == 'multiple_choice'">{{ $t('message.multiple_choice') }}</span>
                <span v-else-if="i.answerType == 'textarea'">{{ $t('message.textarea') }}</span>
                <span v-else>{{ $t('message.text') }}</span>
                <v-card v-if="i.answerType == 'multiple_choice'">
                  <v-card-text class="supporting-text">
                    {{ $t('message.at_least_2_options') }}
                  </v-card-text>
                  <v-card-text>
                    <v-text-field v-bind:key="'existing_question_option_' + index + '_' + index2"
                      v-for="(o, index2) in i.answerOptions" :label="$t('message.answer_option') + ' ' + (index2 + 1)"
                      v-model="i.answerOptions[index2]" maxlength="30" counter>
                    </v-text-field>
                  </v-card-text>
                </v-card>
              </v-card-text>
              <v-card-actions class="pa-0">
                <v-checkbox :label="$t('message.required')" v-model="i.required"></v-checkbox>
                <v-spacer></v-spacer>
                <v-btn elevation="0" v-if="index != 0" icon small @click="moveQuestionOrder(index, index - 1)">
                  <v-icon>mdi-arrow-up-bold-circle-outline</v-icon>
                </v-btn>
                <v-btn elevation="0" v-if="index != questions.length - 1" icon small
                  @click="moveQuestionOrder(index, index + 1)">
                  <v-icon>mdi-arrow-down-bold-circle-outline</v-icon>
                </v-btn>
                <v-btn elevation="0" icon small @click="removeQuestion(index)">
                  <v-icon>mdi-trash-can-outline</v-icon>
                </v-btn>
              </v-card-actions>
              <v-divider></v-divider>
            </v-card>
          </template>
        </div>
      </v-card>
      <v-card-text v-if="!document.survey" class="text-left">
        <div>
          <a @click="displayExtraFieldsDiv = !displayExtraFieldsDiv">
            <v-icon v-if="!displayExtraFieldsDiv" size="15" class="mr-1">mdi-plus-circle</v-icon>
            <v-icon v-else size="15" class="mr-1">mdi-minus-circle</v-icon>
            {{ $t('message.add_custom_fields') }}
          </a>
        </div>
        <v-card class="pa-2" outlined elevation="0" v-if="displayExtraFieldsDiv">
          <v-btn elevation="0" class="mt-4" small @click="addNewExraField()">
            <v-icon>mdi-plus-circle-outline</v-icon>{{ $t('message.add') }}
          </v-btn>
          <v-list two-line>
            <template v-for="(field, index) in extraFields">
              <v-list-item :key="'extra_field_' + index">
                <v-list-item-action>
                  <span class="text-subtitle-1">{{ index + 1 }}</span>
                </v-list-item-action>
                <v-list-item-content class="text-left">
                  <v-list-item-title>
                    <v-text-field :label="$t('message.name')" v-model="field.fieldName"></v-text-field>
                  </v-list-item-title>
                  <v-list-item-subtitle class="mt-0 pt-0">
                    <v-checkbox :label="$t('message.required')" v-model="field.required"></v-checkbox>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn elevation="0" icon small @click="removeExtraField(index)">
                    <v-icon size="20">mdi-trash-can-outline</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="index + 1 < extraFields.length" :key="index"></v-divider>
            </template>
          </v-list>
        </v-card>
      </v-card-text>
      <v-card-actions v-if="!isSmallerDisplay()">
        <v-btn outlined elevation="0" small v-if="documentId == 'new' && this.document.name" color="primary"
          v-allowed="'create_waiver'" @click="creatDocument()">{{ $t('message.create') }}</v-btn>
        <v-btn outlined elevation="0" v-allowed="'create_waiver'" small v-else-if="this.document.name" color="primary"
          @click="updateDocument()">{{ $t('message.update') }}</v-btn>
        <v-btn elevation="0" small class="outlined-btn ml-2" @click="goBack()">{{ $t('message.back') }}</v-btn>
        <v-spacer></v-spacer>
        <v-btn elevation="0" outlined small v-if="documentId != 'new' && !document.survey" color="primary"
          @click="showPreview()">
          <v-icon>mdi-note-search-outline</v-icon>
          {{ $t('message.preview') }}
        </v-btn>
        <v-btn elevation="0" color="primary" outlined small v-allowed="'send_waiver'" v-if="documentId != 'new'"
          @click="sendToAllDialog = true">
          <v-icon>mdi-cellphone-sound</v-icon>
          {{ $t('message.send_to_all') }}
        </v-btn>
        <v-btn elevation="0" outlined small v-if="!isSmallerDisplay() && documentId != 'new' && !document.survey"
          color="primary" @click="showPrintable()">
          <v-icon>mdi-printer-pos-outline</v-icon>
          {{ $t('message.print') }}
        </v-btn>
        <v-btn :disabled="document.required" elevation="0" small v-if="documentId != 'new'" color="error"
          v-allowed="'create_waiver'" @click="showArchiveDialog()">{{ $t('message.archive') }}</v-btn>
      </v-card-actions>
      <div v-else>
        <v-btn class="mt-1" block elevation="0" small v-if="documentId == 'new' && this.document.name" color="primary"
          v-allowed="'create_waiver'" @click="creatDocument()">{{ $t('message.create') }}</v-btn>
        <v-btn class="mt-1" block elevation="0" v-allowed="'create_waiver'" outlined small
          v-else-if="this.document.name" color="primary" @click="updateDocument()">{{ $t('message.update') }}</v-btn>
        <v-btn class="mt-1" block elevation="0" outlined small v-if="documentId != 'new' && !document.survey"
          color="primary" @click="showPreview()">
          <v-icon>mdi-note-search-outline</v-icon>
          {{ $t('message.preview') }}
        </v-btn>
        <v-btn class="mt-1" color="primary" outlined block elevation="0" small v-allowed="'send_waiver'"
          v-if="documentId != 'new'" @click="sendToAllDialog = true">
          <v-icon>mdi-cellphone-sound</v-icon>
          {{ $t('message.send_to_all') }}
        </v-btn>
        <v-btn :disabled="document.required" class="mt-1" block elevation="0" small v-if="documentId != 'new'"
          color="error" v-allowed="'create_waiver'" @click="showArchiveDialog()">{{ $t('message.archive') }}</v-btn>
      </div>
    </div>
    <v-dialog v-model="editLanguageDialog">
      <v-card class="pa-2" outlined>
        <h2 class="ml-6 mt-2 mb-4">
          {{ addingNewLanguage ? $t('message.add') : $t('message.edit') }}
        </h2>
        <v-card-text>
          <v-text-field dense outlined :label="$t('message.language')" v-model="languageField"
            maxlength="2"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="!addingNewLanguage" elevation="0" small color="primary" @click.stop="updatedSelectedLang()">{{
            $t('message.update') }}</v-btn>
          <v-btn v-else elevation="0" small color="primary" @click.stop="addNewLang()">{{
            $t('message.add') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn elevation="0" small @click.stop="editLanguageDialog = false; addingNewLanguage = false">{{
            $t('message.close') }}</v-btn>
          <v-btn elevation="0" small color="error" @click.stop="removeTranslation()">{{ $t('message.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="sendToAllDialog" max-width="50%">
      <v-card outlined class="pa-2">
        <v-card-text>
          <div class="text-subtitle-1">{{ $t('message.send_to_all_title') }}</div>
          <div>{{ $t('message.send_to_all_explained') }}</div>
        </v-card-text>
        <v-card-actions>
          <v-btn :loading="sendingToAll" elevation="0" small :disabled="sendingToAll" color="primary" outlined
            @click.stop="sendToAllMembers()">
            {{ $t('message.send') }}
          </v-btn>
          <v-btn elevation="0" small @click.stop="sendToAllDialog = false">{{ $t('message.close')
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="archiveDialog" max-width="50%">
      <v-card outlined class="pa-2">
        <v-card-text>
          <h3>{{ $t('message.are_you_sure') }}</h3>
        </v-card-text>
        <v-card-actions>
          <v-btn elevation="0" small color="error" @click.stop="updateDocument(true)">{{ $t('message.archive')
          }}</v-btn>
          <v-btn elevation="0" small color="secondary" @click.stop="archiveDialog = false">{{ $t('message.close')
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="previewDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card outlined>
        <v-toolbar flat class="opposite--text" color="primary" dense>
          <v-spacer></v-spacer>
          <v-btn color="opposite" icon small @click="previewDialog = false;">
            <v-icon>mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card elevation="0">
          <div v-if="!showIframe" class="text-center pa-2">
            <v-progress-circular :size="50" color="primary" indeterminate></v-progress-circular>
          </div>
          <iframe style="border:none;" v-show="showIframe" :height="previewHeight" width="100%"
            :src="previewUrl"></iframe>
        </v-card>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import Api from "@/services/Api";
import { Environment } from "@/services/Environment";
import { VueEditor } from "vue2-editor";

export default {
  components: {
    CirclesLoader,
    VueEditor,
  },
  data() {
    return {
      documentId: this.$route.params.documentId,
      document: null,
      editLanguageDialog: null,
      addingNewLanguage: false,
      previewDialog: null,
      loading: true,
      sendToAllDialog: false,
      availableLanguages: [],
      displayExtraFieldsDiv: false,
      extraFields: [],
      languageField: null,
      selectedLang: null,
      editorToolbar: this.getEditorToolbar(true),
      documentContents: null,
      archiveDialog: null,
      primaryLanguage: null,
      newQuestion: null,
      previewHeight: null,
      questions: null,
      sendingToAll: false,
      showIframe: false,
      previewUrl: null,
      rules: [v => v && v.length <= 25 || 'Max 25 characters'],
      answerTypes: [
        { text: this.$t('message.text'), value: 'text' },
        { text: this.$t('message.textarea'), value: 'textarea' },
        { text: this.$t('message.true_false'), value: 'boolean' },
        { text: this.$t('message.multiple_choice'), value: 'multiple_choice' }],
    };
  },
  created() {
    if (this.documentId == "new") {
      Api()
        .get("admins/academy/" + this.$store.state.selectedAcademy)
        .then((response) => {
          this.document = {
            lang: response.data.preferredLanguage || this.$i18n.locale,
            active: true,
            required: false,
            name: null,
            managedAcademy: this.$store.state.managedAcademy,
            contentTransaltions: {},
          };
          this.init();
        });
    } else {
      this.getDocument();
    }
    this.loading = false;
  },
  watch: {},
  methods: {
    orderLanguages() {
      const newLangCode = this.primaryLanguage;
      this.switchLang(newLangCode);
      const doc = this.document;
      if (!doc.contentTransaltions || !doc.contentTransaltions[newLangCode])
        return;
      const currentPrimaryLang = doc.lang;
      const currentPrimaryContents = doc.contents;
      const newPrimaryContents = doc.contentTransaltions[newLangCode];
      doc.lang = newLangCode;
      doc.contents = newPrimaryContents;
      doc.contentTransaltions[currentPrimaryLang] = currentPrimaryContents;
      delete doc.contentTransaltions[newLangCode];
      this.selectedLang = newLangCode;
    },
    copyPublicUrl() {
      const baseUrl = process.env.VUE_APP_API_HOST;
      const urlToCopy = baseUrl + '/pub/academy/' + this.$store.state.selectedAcademy + '/document/' + this.documentId;
      this.copyToClipboard(urlToCopy);
    },
    showPreview() {
      this.previewHeight = (window.innerHeight - 60) + 'px';
      this.showIframe = false;
      this.previewDialog = true;
      const vm = this;
      setTimeout(function () {
        vm.showIframe = true;
      }, 2000);
    },
    displayAddNewLangDialog() {
      this.addingNewLanguage = true;
      this.editLanguageDialog = true;
      this.languageField = null;
    },
    updatedSelectedLang() {
      const tmpContents = this.documentContents;
      const oldLang = this.selectedLang;
      const lang = this.languageField;
      const index = this.availableLanguages.indexOf(oldLang);
      this.availableLanguages[index] = lang;
      this.selectedLang = lang;
      this.previewUrl = Environment.get("VUE_APP_ASSETS_HOME") + 'secure/documents/templates/print/' + this.documentId + '/' + lang + '?preview=true';
      delete this.document.contentTransaltions[oldLang];
      this.document.contentTransaltions[lang] = tmpContents;
      this.editLanguageDialog = false;
      if (this.document.lang == oldLang) {
        this.document.lang = lang;//update primary lang for this document
        this.document.contents = this.documentContents;
      }
    },
    removeExtraField(index) {
      this.extraFields.splice(index, 1);
    },
    addNewExraField() {
      this.extraFields.push({ fieldName: null, required: false });
    },
    showArchiveDialog() {
      this.archiveDialog = true;
    },
    initializeNewQuestion() {
      this.newQuestion = {
        answerType: 'text',
        answerOptions: ['', '', '', '', '']
      }
    },
    editQuestion(index) {
      this.questions[index] = this.newQuestion;
      this.newQuestion = null;
    },
    removeQuestion(index) {
      if (this.questions)
        this.questions.splice(index, 1);
    },
    moveQuestionOrder(from, to) {
      let arr = this.questions;
      if (arr) {
        let swap = arr[to];
        arr[to] = arr[from];
        arr[from] = swap;
        this.questions = null;
        this.questions = arr;
      }
    },
    addNewQuestion() {
      if (this.newQuestion) {
        if (!this.questions)
          this.questions = [];
        this.questions.push(this.newQuestion);
        this.newQuestion = null;
      }
    },
    sendToAllMembers() {
      this.sendingToAll = true;
      Api()
        .post(
          "documents/academy/" +
          this.$store.state.managedAcademy +
          "/templates/send/" +
          this.documentId,
          { users: "all", objectType: 'document' }
        )
        .then((response) => {
          if (response) {
            this.showAlert(
              "success",
              this.$t("message.sent_to_members", [response.data.total])
            );
            this.sendToAllDialog = false;
            this.sendingToAll = false;
          }
        })
        .catch(() => {
          this.sendingToAll = false;
          this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    init() {
      this.availableLanguages = [this.document.lang];
      if (this.documentId != "new") {
        for (const lang in this.document.contentTransaltions) {
          if (this.availableLanguages.indexOf(lang) < 0 && this.document.contentTransaltions[lang])
            this.availableLanguages.push(lang);
        }
      }
      this.selectedLang = this.document.lang;
      this.primaryLanguage = this.document.lang;
      if (this.document.contents)
        this.documentContents = this.document.contents.replace(/<div/g, "<p")
    },
    switchLang(lang) {
      this.incporporateLangContents();
      this.selectedLang = lang;
      this.previewUrl = Environment.get("VUE_APP_ASSETS_HOME") + 'secure/documents/templates/print/' + this.documentId + '/' + lang + '?preview=true';
      if (lang == this.document.lang) {
        this.documentContents = this.document.contents;
      } else {
        this.documentContents = this.document.contentTransaltions[lang];
      }
    },
    addNewLang() {
      this.incporporateLangContents();
      let lang = this.languageField;
      if (!lang) {
        this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        return;
      }
      lang = lang.trim();
      lang = lang.toLowerCase();
      if (this.availableLanguages.indexOf(lang) < 0) {
        this.availableLanguages.push(lang);
        if (!this.document.contentTransaltions) {
          this.document.contentTransaltions = {};
        }
        this.document.contentTransaltions[lang] = this.$t("message.enter_text");
        this.documentContents = this.$t("message.enter_text");
        this.selectedLang = lang;
        this.editLanguageDialog = false;
        this.languageField = null;
      } else {
        this.showAlert("error", this.$t("message.already_present"));
      }
    },
    removeTranslation() {
      if (this.selectedLang == this.document.lang) {
        this.showAlert("error", this.$t("message.primary_language"));
        return;
      }
      const index = this.availableLanguages.indexOf(this.selectedLang);
      if (index >= 0) this.availableLanguages.splice(index, 1);
      let updatedTransaltions = {};
      for (const k of Object.keys(this.document.contentTransaltions)) {
        if (k != this.selectedLang)
          updatedTransaltions[k] = this.document.contentTransaltions[k];
      }
      this.documentfContents = null;
      this.editLanguageDialog = false;
      this.document.contentTransaltions = updatedTransaltions;
      this.switchLang(this.document.lang);
    },
    showPrintable() {
      window.open(
        Environment.get("VUE_APP_ASSETS_HOME") + "secure/documents/templates/print/" +
        this.documentId +
        "/" +
        this.selectedLang,
        "_blank"
      );
    },
    getDocument() {
      Api()
        .get(
          "documents/academy/" +
          this.$store.state.managedAcademy +
          "/templates/" +
          this.documentId
        )
        .then((response) => {
          if (response) {
            this.document = response.data;
            this.previewUrl = Environment.get("VUE_APP_ASSETS_HOME") + 'secure/documents/templates/print/' + this.documentId + '/' + (this.selectedLang ? this.selectedLang : 'en') + '?preview=true';
            if (this.document.extraFields) {
              this.extraFields = this.document.extraFields;
              if (this.extraFields.length > 0)
                this.displayExtraFieldsDiv = true;
            }
            this.init();
            if (this.document.questions && this.document.questions.length > 0)
              this.questions = this.document.questions;
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    incporporateLangContents() {
      if (this.documentContents) {
        if (this.selectedLang == this.document.lang) {
          this.document.contents = this.documentContents;
        } else {
          this.document.contentTransaltions[
            this.selectedLang
          ] = this.documentContents;
        }
      }
    },
    creatDocument() {
      if (!this.document.name) {
        this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        return;
      }
      this.incporporateLangContents();
      if (this.questions && this.questions.length > 0)
        this.document.questions = this.questions;
      if (this.document.contract)
        this.document.survey = false;
      if (this.extraFields && this.extraFields.length > 0)
        this.document.extraFields = this.extraFields;
      Api()
        .post(
          "documents/academy/" +
          this.$store.state.managedAcademy +
          "/templates",
          this.document
        )
        .then((response) => {
          if (response) {
            this.documentId = response.data._id;
            this.showAlert("success", this.$t("message.success"));
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    updateDocument(archive) {
      this.incporporateLangContents();
      if (this.questions && this.questions.length > 0)
        this.document.questions = this.questions;
      if (archive) {
        this.document.active = false;
        this.document.archived = true;
        this.document.required = false;
      }
      if (this.document.required)
        this.document.public = false;
      if (this.extraFields && this.extraFields.length > 0)
        this.document.extraFields = this.extraFields;
      Api()
        .patch(
          "documents/academy/" +
          this.$store.state.managedAcademy +
          "/templates/" +
          this.documentId,
          this.document
        )
        .then((response) => {
          if (response) {
            if (archive)
              this.navigateTo({ name: 'waivers' })
            else
              this.showAlert("success", this.$t("message.success"));
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
  },
};
</script>

<style scoped></style>
